/*
 Scroll-position handler for the browser window

 Author: Anders Gissel <agi@dis-play.dk>
 ************************************************************************************************************

 This module exposes a set of tools to detect when scrolling has occurred, and to return the current scroll
 position to whatever scripts need them. Only works on window, and not usable for individual elements.

 The callbacks are throttled to avoid using too many CPU cycles.


 To use:

     import onScroll from './utils/onScroll';

     function scrollHasHappened() {}

     onScroll(scrollHasHappened);


 To get the scroll position:

     import { currentWindowScrollPositionX, currentWindowScrollPositionY, default as onScroll } from './utils/onScroll';

     function scrollHasHappened() {
         window.console.log(currentWindowScrollPositionX, currentWindowScrollPositionY);
     }

     onScroll(scrollHasHappened);

 */





import underscoreLite from './underscoreLite';
import onReady from './onReady';
import siteSettings from '../../site-settings.json';

let functionQueue = [];
const doc = document.documentElement;


/**
 * Current horizontal scroll position
 * @type {number}
 */
export let currentWindowScrollPositionX = 0;


/**
 * Current vertical scroll position
 * @type {number}
 */
export let currentWindowScrollPositionY = 0;



/**
 * Get the currenct scroll position on both axes.
 *
 * @returns {[Number,Number]}
 */
export function getScrollPosition() {
    currentWindowScrollPositionX = (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
    currentWindowScrollPositionY = (window.pageYOffset || doc.scrollTop)  - (doc.clientTop || 0);

    return [currentWindowScrollPositionX, currentWindowScrollPositionY];
}



/**
 * This function is run every time the window is scrolled. It's on a throttle, though, so it won't be fired too
 * often.
 */
function performCalculationCallback() {
    // Get our current window size(s)
    getScrollPosition();

    functionQueue.forEach(funcRef => {
        funcRef([currentWindowScrollPositionX, currentWindowScrollPositionY]);
    });
}

// Set up a throttle, so our callback function is fired a maximum of once per second.
const throttledScrollResponse = siteSettings.onScrollThrottleTimeout ? underscoreLite.throttle(performCalculationCallback, siteSettings.onScrollThrottleTimeout) : performCalculationCallback;




// Utilize our prototyped "ready"-function to bind an event for handling window scroll events.
onReady(() => {
    window.addEventListener("scroll", throttledScrollResponse);

    // Get our current scroll positions, because we might need the data.
    getScrollPosition();
}, 50);




/**
 * Remove callback function from callback stack.
 *
 * @param {function} callback
 */
export function removeCallback(callback) {
    if (typeof callback === "function") {
        functionQueue = underscoreLite.filter(functionQueue, functionReference => {
            return functionReference !== callback;
        });
    }
}




/**
 * Store a function reference to be fired every time the window is resized.
 *
 * @param {function} callback
 */
export function onScroll(callback) {
    if (typeof callback === "function") {
        functionQueue.push(callback);
    }
}


export default onScroll;